import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { Tool, getAllTools, setCatalogTools } from '../data/tools';

/** Raw row shape returned by GET /api/v1/tools (see api/tools.ts). */
export interface CatalogToolRow {
  id: string;
  slug: string;
  name: string;
  category: Tool['category'];
  subcategory: string | null;
  description: string;
  website: string;
  hipaa_compliant: boolean;
  pricing: string | null;
  pricing_tier: Tool['pricingTier'] | null;
  best_for: string | null;
  key_features: string[] | null;
  keywords: string[] | null;
  video_url: string | null;
  guide_notes: string | null;
  created_at: string;
  updated_at: string;
}

export function rowToTool(row: CatalogToolRow): Tool {
  return {
    id: row.slug,
    catalogId: row.id,
    source: 'catalog',
    name: row.name,
    category: row.category,
    subcategory: row.subcategory ?? undefined,
    description: row.description,
    website: row.website,
    hipaaCompliant: row.hipaa_compliant,
    pricing: row.pricing ?? 'See Website For Pricing.',
    pricingTier: row.pricing_tier ?? 'medium',
    bestFor: row.best_for ?? '',
    keyFeatures: row.key_features ?? [],
    keywords: row.keywords ?? [],
    painPoints: [],
    setupDifficulty: 'medium',
    setupTime: 'Varies',
    videoUrl: row.video_url ?? undefined,
    guideNotes: row.guide_notes ?? undefined,
  };
}

interface CatalogContextType {
  /** Built-in tools merged with DB catalog tools (DB wins on id collision). */
  allTools: Tool[];
  loading: boolean;
  refreshCatalog: () => Promise<void>;
}

const CatalogContext = createContext<CatalogContextType>({
  allTools: getAllTools(),
  loading: false,
  refreshCatalog: async () => {},
});

export function useCatalog() {
  return useContext(CatalogContext);
}

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [allTools, setAllTools] = useState<Tool[]>(getAllTools());
  const [loading, setLoading] = useState(false);

  const refreshCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/tools');
      if (res.ok) {
        const rows = (await res.json()) as CatalogToolRow[];
        setCatalogTools(rows.map(rowToTool));
        setAllTools(getAllTools());
      }
      // Non-OK (e.g. table not migrated yet) → keep built-in tools silently.
    } catch (err) {
      console.error('Failed to fetch catalog tools:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCatalog();
  }, [refreshCatalog]);

  return (
    <CatalogContext.Provider value={{ allTools, loading, refreshCatalog }}>
      {children}
    </CatalogContext.Provider>
  );
}
